import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/' },
    { 
      name: 'Trading', 
      href: '/trading',
      hasDropdown: true,
      dropdownItems: [
        { name: 'Trading Platform', href: '/trading' },
        { name: 'Copy Trading', href: '/copy-trading' },
        { name: 'Portfolio', href: '/portfolio' },
      ]
    },
    { 
      name: 'Markets', 
      href: '/markets',
      hasDropdown: true,
      dropdownItems: [
        { name: 'All Markets', href: '/markets' },
        { name: 'Stocks', href: '/markets?type=stocks' },
        { name: 'Cryptocurrency', href: '/markets?type=crypto' },
        { name: 'Forex', href: '/markets?type=forex' },
        { name: 'Commodities', href: '/markets?type=commodities' },
      ]
    },
    { 
      name: 'Research', 
      href: '/news',
      hasDropdown: true,
      dropdownItems: [
        { name: 'Market Analysis', href: '/news' },
        { name: 'Market News', href: '/news' },
      ]
    },
    { 
      name: 'Company', 
      href: '/about',
      hasDropdown: true,
      dropdownItems: [
        { name: 'About Us', href: '/about' },
        { name: 'Contact', href: '/contact' },
      ]
    },
  ];

  const isActiveLink = (href) => {
    return location.pathname === href;
  };

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const shouldBeScrolled = scrollTop > 100;
      
      if (shouldBeScrolled !== isScrolled) {
        setIsScrolled(shouldBeScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Check initial scroll position
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isScrolled]);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out ${
      isScrolled 
        ? 'bg-white shadow-lg border-b border-gray-200' 
        : 'bg-white shadow-sm border-b border-gray-100'
    }`}>
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" aria-label="Top">
        <div className={`flex w-full items-center justify-between transition-all duration-300 ease-in-out ${
          isScrolled ? 'py-2 lg:py-3' : 'py-3 lg:py-4'
        }`}>
          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <Link to="/" className="flex items-center">
              <img 
                src="https://res.cloudinary.com/dq0hqpe7f/image/upload/v1750281707/crestedgeloho-dark_qnwxyl.png"
                alt="Crest Edge Brokerage Logo"
                className={`transition-all duration-300 ease-in-out ${
                  isScrolled ? 'h-7 lg:h-9' : 'h-8 lg:h-10'
                }`}
                style={{ width: 'auto' }}
              />
            </Link>
          </div>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex lg:items-center lg:justify-center flex-1 mx-2 xl:mx-4">
            <div className="flex items-center space-x-4 xl:space-x-6">
              {navigation.map((item) => (
                <div key={item.name} className="relative">
                  {item.hasDropdown ? (
                    <div 
                      className="relative"
                      onMouseEnter={() => setOpenDropdown(item.name)}
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                      <button className="nav-link flex items-center space-x-1">
                        <span>{item.name}</span>
                        <ChevronDownIcon className="h-4 w-4" />
                      </button>
                      <AnimatePresence>
                        {openDropdown === item.name && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="absolute left-0 top-full mt-2 w-48 rounded-md bg-white py-2 shadow-lg ring-1 ring-black ring-opacity-5 z-50"
                          >
                            {item.dropdownItems?.map((dropdownItem) => (
                              <Link
                                key={dropdownItem.name}
                                to={dropdownItem.href}
                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                                onClick={() => setOpenDropdown(null)}
                              >
                                {dropdownItem.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className={isActiveLink(item.href) ? 'nav-link-active' : 'nav-link'}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Desktop Auth Buttons */}
          <div className="hidden lg:flex lg:items-center lg:space-x-3 flex-shrink-0">
            <a href="http://dashboard.crestedgebrokerage.com/" className="text-gray-600 hover:text-gray-900 px-2 py-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap">
              Sign In
            </a>
            <a href="http://dashboard.crestedgebrokerage.com/" className="btn-primary text-sm px-4 py-2 whitespace-nowrap">
              Get Started
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="flex lg:hidden">
            <button
              type="button"
              className={`inline-flex items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 transition-all duration-200 ${
                isScrolled ? 'p-1.5' : 'p-2'
              }`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <span className="sr-only">Open main menu</span>
              {isMenuOpen ? (
                <XMarkIcon className={`transition-all duration-200 ${
                  isScrolled ? 'h-5 w-5' : 'h-6 w-6'
                }`} />
              ) : (
                <Bars3Icon className={`transition-all duration-200 ${
                  isScrolled ? 'h-5 w-5' : 'h-6 w-6'
                }`} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden bg-white border-t border-gray-100 shadow-lg"
            >
              <div className="px-4 py-4 space-y-1 max-h-96 overflow-y-auto">
                {navigation.map((item) => (
                  <div key={item.name}>
                    {item.hasDropdown ? (
                      <>
                        <Link
                          to={item.href}
                          className={`block py-3 px-4 text-base font-medium rounded-lg transition-colors duration-200 ${
                            isActiveLink(item.href) 
                              ? 'text-primary-600 bg-primary-50' 
                              : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                          }`}
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {item.name}
                        </Link>
                        <div className="ml-4 mt-1 space-y-1">
                          {item.dropdownItems?.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.name}
                              to={dropdownItem.href}
                              className="block py-2 px-4 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                              onClick={() => setIsMenuOpen(false)}
                            >
                              {dropdownItem.name}
                            </Link>
                          ))}
                        </div>
                      </>
                    ) : (
                      <Link
                        to={item.href}
                        className={`block py-3 px-4 text-base font-medium rounded-lg transition-colors duration-200 ${
                          isActiveLink(item.href) 
                            ? 'text-primary-600 bg-primary-50' 
                            : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                        }`}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    )}
                  </div>
                ))}
                <div className="mt-6 pt-4 border-t border-gray-100 space-y-3">
                  <a
                    href="http://dashboard.crestedgebrokerage.com/"
                    className="block w-full text-center py-3 px-4 text-base font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Sign In
                  </a>
                  <a
                    href="http://dashboard.crestedgebrokerage.com/"
                    className="block w-full text-center btn-primary"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Get Started
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Header; 